/**
 * A room's Follow-ups in Season: the automations that run on it, each with a switch for this room alone, the invites it
 * sent.
 */
import { Link } from "react-router";
import type { AutomationSummary, WebinarDetail } from "../../../shared/types";
import { InvitesCard } from "../../components/InvitesCard";
import { GroupedList } from "../../components/season/GroupedList";
import { GroupHead } from "../../components/season/PageHead";
import { Icon } from "../../components/ui/Icon";
import { Toggle } from "../../components/ui/Toggle";
import { useToast } from "../../components/ui/Toast";
import { useAutomationSwitch, useRoomAutomations } from "./useFollowups";
import { useInviteSends } from "./useRoom";

const n = (v: number) => v.toLocaleString("en-US");

function Row({ a, roomId, onSaved }: { a: AutomationSummary; roomId: string; onSaved: (a: AutomationSummary) => void }) {
  const { on, busy, flip, running, now } = useAutomationSwitch(a, roomId, onSaved);
  return (
    <li className="k-lr rm-fr">
      <Link className="k-lr-b" to={`/automations/${a.id}`}>
        <span className="k-ico">
          <Icon name="zap" />
        </span>
        <span className="k-lr-t">
          <b>{a.name}</b>
          <span className="k-clamp">{a.trigger_label}</span>
          {a.attention ? <span className="rm-warn">{a.attention}</span> : null}
          {now ? (
            <span>
              <b>{n(now)}</b> in it now
            </span>
          ) : null}
          <span className={on ? "rm-state on" : "rm-state"}>{running ? (on ? "On for this room" : "Off for this room") : "Off everywhere"}</span>
        </span>
      </Link>
      {running ? <Toggle on={on} onChange={flip} label={`${a.name} on this room`} disabled={busy} /> : null}
    </li>
  );
}

export function FollowupsSection({ detail }: { detail: WebinarDetail }) {
  const toast = useToast();
  const { sends, put } = useInviteSends(detail.id);
  const { autos, failed, put: putAuto } = useRoomAutomations(detail.id);
  const live = detail.live;
  const canRemind = !live || (live.series ? !live.series.ended : live.state === "scheduled");
  return (
    <>
      <GroupHead
        title="Follow-ups on this room"
        action={
          <Link className="k-link" to="/automations">
            <Icon name="plus" /> Add a follow-up
          </Link>
        }
      />
      {autos === null ? (
        <p className="k-rm">{failed ? "We couldn't load the follow-ups." : "Loading"}</p>
      ) : autos.length ? (
        <GroupedList className="rm-fl">
          {autos.map((a) => (
            <Row key={a.id} a={a} roomId={detail.id} onSaved={putAuto} />
          ))}
        </GroupedList>
      ) : (
        <p className="k-rm">No follow-ups run on this room yet.</p>
      )}

      {sends.length ? (
        <div className="rm-gap">
          <InvitesCard webinarId={detail.id} sends={sends} onChanged={put} onToast={toast} canRemind={canRemind} />
        </div>
      ) : null}

    </>
  );
}
